// ============================================================================
// FILE:    src/app/services/capability.service.ts
// VERSION: v2.0.0 — RBAC Phase 2: role_key primär, perm-Fallback
// DATE:    2026-05-09
//
// ZWECK:
//   Zentrale API für alle Capability-Checks im UI.
//   Beantwortet EINE Frage: "Darf dieser User diese Funktion nutzen?"
//
//   Wird genutzt von:
//     - has-capability.directive.ts  (*hasCapability="'SPRINT_CREATE'")
//     - project-access.guard.ts      (blockt Navigation)
//     - project-select HTML          (btn-Sichtbarkeit via Direktive)
//     - project-dashboard HTML       (Sektor-/Card-Sichtbarkeit)
//
// ENTSCHEIDUNGSLOGIK (in dieser Reihenfolge):
//
//   1. Super-Admin-Bypass (orgPerm >= 7):
//      → true für ALLES, sofort. Kein weiterer Check.
//
//   2. Org-Capability (scope='org' oder kein pslot):
//      → orgPermToRole() → ORG_ROLE_CAPABILITIES[role].has(cap)
//
//   3. Projekt-Capability (scope='project', pslot vorhanden):
//      3a. role_key aus projectRoles Map (PRIMÄR, v2.0.0+):
//          → roleKeyToRole() → PROJECT_ROLE_CAPABILITIES[role].has(cap)
//      3b. Fallback: perm aus projectPerms (Legacy):
//          → projectPermToRole() → PROJECT_ROLE_CAPABILITIES[role].has(cap)
//      3c. ODER: Org-Capability (PMO_OFFICER hat PROJECT_ACCESS_MANAGE org-weit)
//
// PMO-OFFICER BYPASS für PROJECT_ACCESS_MANAGE:
//   Der PMO Officer (orgPerm >= 3) hat PROJECT_ACCESS_MANAGE in seinen
//   ORG_ROLE_CAPABILITIES. Das bedeutet: er kann manage-access für ALLE
//   Projekte öffnen, auch wenn er kein explizites Projekt-Mitglied ist.
//   hasCapability() prüft BEIDE Ebenen (Projekt + Org) — das Org-Check
//   greift als Fallback wenn der Projekt-Check false ergibt.
//
// ÄNDERUNGEN v2.0.0 gegenüber v1.x:
//   - computeProjectCapabilities() nutzt jetzt role_key primär (roleKeyToRole)
//   - perm-Mapping als Fallback wenn kein role_key vorhanden
//   - Org-Capabilities als zweite Prüfebene in hasCapability()
// ============================================================================

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { Capability } from '../core/auth/capabilities.enum';
import { Role } from '../core/auth/roles.enum';
import {
  PROJECT_ROLE_CAPABILITIES,
  ORG_ROLE_CAPABILITIES
} from '../core/auth/role-capabilities.map';
import {
  projectPermToRole,
  orgPermToRole,
  roleKeyToRole
} from '../core/auth/permission-role.map';
import { PermissionService } from './permission.service';

@Injectable({ providedIn: 'root' })
export class CapabilityService {

  /**
   * Reaktiver Stream der Org-Capabilities.
   * Wird von HasCapabilityDirective subscribed um auf Änderungen zu reagieren.
   */
  public orgCapabilities$: Observable<Set<Capability>>;

  constructor(private permissions: PermissionService) {
    this.orgCapabilities$ = this.permissions.snapshot$.pipe(
      map(snap => {
        if (!snap) return new Set<Capability>();
        return this.computeOrgCapabilities(snap.orgPermission);
      }),
      distinctUntilChanged((a, b) => a.size === b.size && [...a].every(c => b.has(c))),
      shareReplay(1)
    );
  }

  /**
   * Reaktiver Stream der Projekt-Capabilities für einen pslot.
   * Reagiert auf Snapshot-Änderungen (z.B. nach Permission-Upgrade durch Admin).
   */
  projectCapabilities$(pslot: string): Observable<Set<Capability>> {
    return this.permissions.snapshot$.pipe(
      map(snap => {
        if (!snap) return new Set<Capability>();
        if (snap.isSuperAdmin) return new Set<Capability>(Object.values(Capability));
        return this.computeProjectCapabilities(
          snap.projectPerms.get(pslot) ?? 0,
          snap.projectRoles.get(pslot)   // NEU v2.0.0: role_key übergeben
        );
      }),
      distinctUntilChanged((a, b) => a.size === b.size && [...a].every(c => b.has(c))),
      shareReplay(1)
    );
  }

  // ── Synchrone API — das nutzen Guard und Direktive ────────────────────────

  /**
   * Prüft ob der aktuelle User eine Capability hat.
   *
   * Prüfreihenfolge:
   *   1. Super-Admin-Bypass
   *   2. Projekt-Capabilities (wenn pslot angegeben)
   *   3. Org-Capabilities (immer als Fallback — PMO hat z.B. PROJECT_ACCESS_MANAGE)
   *
   * @param cap     Die zu prüfende Capability
   * @param pslot   Optional: Projekt-Kontext. Null/undefined = nur Org-Check.
   */
  hasCapability(cap: Capability, pslot?: string | null): boolean {
    const snap = this.permissions.getSnapshot();
    if (!snap) return false;

    // 1. Super-Admin: alles erlaubt
    if (snap.isSuperAdmin) return true;

    // 2. Projekt-Check (wenn pslot vorhanden)
    if (pslot) {
      const projectCaps = this.computeProjectCapabilities(
        snap.projectPerms.get(pslot) ?? 0,
        snap.projectRoles.get(pslot)    // NEU v2.0.0: role_key primär
      );
      if (projectCaps.has(cap)) return true;
    }

    // 3. Org-Check als Fallback
    // Wichtig für PMO_OFFICER: er hat PROJECT_ACCESS_MANAGE in seinen
    // ORG_ROLE_CAPABILITIES — greift auch ohne eigenen Projekt-Zugang.
    const orgCaps = this.computeOrgCapabilities(snap.orgPermission);
    return orgCaps.has(cap);
  }

  /** Prüft ob der User ALLE angegebenen Capabilities hat (UND-Verknüpfung). */
  hasAllCapabilities(caps: Capability[], pslot?: string | null): boolean {
    return caps.every(c => this.hasCapability(c, pslot));
  }

  /** Prüft ob der User MINDESTENS EINE der angegebenen Capabilities hat (ODER). */
  hasAnyCapability(caps: Capability[], pslot?: string | null): boolean {
    return caps.some(c => this.hasCapability(c, pslot));
  }

  /** Gibt alle Projekt-Capabilities für einen pslot zurück (synchron). */
  getProjectCapabilities(pslot: string): Set<Capability> {
    const snap = this.permissions.getSnapshot();
    if (!snap) return new Set();
    if (snap.isSuperAdmin) return new Set(Object.values(Capability));
    return this.computeProjectCapabilities(
      snap.projectPerms.get(pslot) ?? 0,
      snap.projectRoles.get(pslot)
    );
  }

  /** Gibt alle Org-Capabilities zurück (synchron). */
  getOrgCapabilities(): Set<Capability> {
    const snap = this.permissions.getSnapshot();
    if (!snap) return new Set();
    return this.computeOrgCapabilities(snap.orgPermission);
  }

  /** Gibt die Projekt-Rolle zurück (für Debugging / UI-Labels). */
  getProjectRole(pslot: string): Role {
    const snap = this.permissions.getSnapshot();
    if (!snap) return Role.PROJECT_IDLE;
    if (snap.isSuperAdmin) return Role.SUPER_ADMIN;

    // Primär: role_key aus Redis
    const roleKey = snap.projectRoles.get(pslot);
    if (roleKey) {
      return roleKeyToRole(roleKey) ?? projectPermToRole(snap.projectPerms.get(pslot) ?? 0);
    }
    // Fallback: perm-Mapping
    return projectPermToRole(snap.projectPerms.get(pslot) ?? 0);
  }

  /** Gibt die Org-Rolle zurück (für Debugging / UI-Labels). */
  getOrgRole(): Role {
    const snap = this.permissions.getSnapshot();
    if (!snap) return Role.NONE;
    return orgPermToRole(snap.orgPermission);
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  /**
   * Berechnet Projekt-Capabilities.
   *
   * PRIMÄR (v2.0.0+): role_key aus Redis → roleKeyToRole() → Capabilities.
   * FALLBACK (Legacy): perm → projectPermToRole() → Capabilities.
   *
   * Begründung für Dual-Path:
   *   Nicht alle Access-Einträge haben sofort einen role_key (Migration).
   *   Der perm-Fallback stellt sicher dass Legacy-Einträge weiter funktionieren.
   */
  private computeProjectCapabilities(perm: number, roleKey?: string | null): Set<Capability> {
    let role: Role;

    if (roleKey) {
      // Primärer Pfad: explizite Rolle aus Redis slot.role_key
      role = roleKeyToRole(roleKey) ?? projectPermToRole(perm);
    } else {
      // Fallback: numerischer perm-Wert (Legacy)
      role = projectPermToRole(perm);
    }

    return new Set(PROJECT_ROLE_CAPABILITIES[role] || []);
  }

  /**
   * Berechnet Org-Capabilities aus orgPermission.
   * orgPermToRole() mappt: 0→NONE, 1→APP_USER, 3+→PMO_OFFICER, 7→SUPER_ADMIN.
   */
  private computeOrgCapabilities(perm: number): Set<Capability> {
    const role = orgPermToRole(perm);
    return new Set(ORG_ROLE_CAPABILITIES[role] || []);
  }
}
